/**
 * Display labels and colour classes for ticket enums. Lookups are
 * case-insensitive so `IN_PROGRESS` (Prisma) and `in_progress` (wire docs) both
 * resolve. Colours come from the Paper artboards; see src/index.css for hex.
 */

export const PRIORITIES = ['p1', 'p2', 'p3', 'p4'] as const
export type Priority = (typeof PRIORITIES)[number]

export const TICKET_STATUSES = [
  'new',
  'in_progress',
  'waiting_on_customer',
  'waiting_on_vendor',
  'scheduled',
  'escalated_engineering',
  'resolved',
  'closed',
] as const
export type TicketStatus = (typeof TICKET_STATUSES)[number]

export const TICKET_TYPES = ['defect', 'integration', 'data_correction', 'service_request', 'question'] as const
export type TicketType = (typeof TICKET_TYPES)[number]

interface PriorityMeta {
  /** Short code, e.g. `P1`. */
  code: string
  /** Name as in analytics (`Critical`, `High`, `Normal`, `Low`). */
  name: string
  /** Pill text on ticket detail, e.g. `P1 · Critical`. */
  label: string
  /** Background class for the 8px queue dot. */
  dotClass: string
  /** Pill classes (P1 is tinted rust; the rest use the neutral pill). */
  pillClass: string
}

const PRIORITY_META: Record<Priority, PriorityMeta> = {
  p1: { code: 'P1', name: 'Critical', label: 'P1 · Critical', dotClass: 'bg-priority-p1', pillClass: 'bg-priority-p1-bg text-priority-p1' },
  p2: { code: 'P2', name: 'High', label: 'P2 · High', dotClass: 'bg-priority-p2', pillClass: 'bg-status-neutral-bg text-status-neutral-fg' },
  p3: { code: 'P3', name: 'Normal', label: 'P3 · Normal', dotClass: 'bg-priority-p3', pillClass: 'bg-status-neutral-bg text-status-neutral-fg' },
  p4: { code: 'P4', name: 'Low', label: 'P4 · Low', dotClass: 'bg-priority-p4', pillClass: 'bg-status-neutral-bg text-status-neutral-fg' },
}

interface StatusMeta {
  label: string
  pillClass: string
}

const STATUS_META: Record<TicketStatus, StatusMeta> = {
  new: { label: 'New', pillClass: 'bg-status-new-bg text-status-new-fg' },
  in_progress: { label: 'In progress', pillClass: 'bg-status-progress-bg text-status-progress-fg' },
  waiting_on_customer: { label: 'Waiting on customer', pillClass: 'bg-status-customer-bg text-status-customer-fg' },
  waiting_on_vendor: { label: 'Waiting on vendor', pillClass: 'bg-status-neutral-bg text-status-neutral-fg' },
  scheduled: { label: 'Scheduled', pillClass: 'bg-status-neutral-bg text-status-neutral-fg' },
  escalated_engineering: { label: 'Escalated to engineering', pillClass: 'bg-status-escalated-bg text-status-escalated-fg' },
  resolved: { label: 'Resolved', pillClass: 'bg-status-resolved-bg text-status-resolved-fg' },
  closed: { label: 'Closed', pillClass: 'bg-status-closed-bg text-status-closed-fg' },
}

const TYPE_LABEL: Record<TicketType, string> = {
  defect: 'Defect',
  integration: 'Integration',
  data_correction: 'Data correction',
  service_request: 'Service request',
  question: 'Question',
}

const FALLBACK_PILL = 'bg-status-neutral-bg text-status-neutral-fg'

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

/** Title-cases an unknown enum (`some_value` → `Some value`) so a new value still renders. */
function humanize(value: string): string {
  const words = value.trim().replace(/_/g, ' ').toLowerCase()
  return words ? words[0].toUpperCase() + words.slice(1) : ''
}

export function normalizePriority(value: string | null | undefined): Priority | null {
  const v = norm(value)
  return (PRIORITIES as readonly string[]).includes(v) ? (v as Priority) : null
}

export function normalizeStatus(value: string | null | undefined): TicketStatus | null {
  const v = norm(value)
  return (TICKET_STATUSES as readonly string[]).includes(v) ? (v as TicketStatus) : null
}

export function normalizeType(value: string | null | undefined): TicketType | null {
  const v = norm(value)
  return (TICKET_TYPES as readonly string[]).includes(v) ? (v as TicketType) : null
}

export function priorityMeta(value: string | null | undefined): PriorityMeta | null {
  const p = normalizePriority(value)
  return p ? PRIORITY_META[p] : null
}

/** `P1`; unknown values pass through upper-cased. */
export function priorityCode(value: string | null | undefined): string {
  return priorityMeta(value)?.code ?? norm(value).toUpperCase()
}

/** `P1 · Critical`. */
export function priorityLabel(value: string | null | undefined): string {
  return priorityMeta(value)?.label ?? norm(value).toUpperCase()
}

export function priorityDotClass(value: string | null | undefined): string {
  return priorityMeta(value)?.dotClass ?? 'bg-priority-p4'
}

export function priorityPillClass(value: string | null | undefined): string {
  return priorityMeta(value)?.pillClass ?? FALLBACK_PILL
}

export function statusLabel(value: string | null | undefined): string {
  const s = normalizeStatus(value)
  return s ? STATUS_META[s].label : humanize(value ?? '')
}

export function statusPillClass(value: string | null | undefined): string {
  const s = normalizeStatus(value)
  return s ? STATUS_META[s].pillClass : FALLBACK_PILL
}

export function typeLabel(value: string | null | undefined): string {
  const t = normalizeType(value)
  return t ? TYPE_LABEL[t] : humanize(value ?? '')
}

/** Queue subject sub-line: `Defect · P1 · Release labels` (component optional). */
export function ticketSubline(type: string, priority: string, component?: string | null): string {
  return [typeLabel(type), priorityCode(priority), component?.trim()].filter(Boolean).join(' · ')
}

/** `D. Mercer` from `Dana Mercer` (queue assignee column); single names pass through. */
export function shortName(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0]
  return `${words[0][0].toUpperCase()}. ${words[words.length - 1]}`
}
